import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigService } from './app-config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import {
  clearUserStorage,
  hasStoredUser,
  isNewBrowserTabContext,
  isSessionHeartbeatStale,
  LS_PREFIX,
  markTabSessionActive,
  TAB_LOGOUT_STORAGE_KEY,
  TAB_PING_STORAGE_KEY,
  TAB_PONG_STORAGE_KEY,
  updateHeartbeat
} from './browser-tab-session-cleanup';
import {
  FHIR_FULL_SITE_STORAGE_KEY,
  FHIR_PATIENT_CONTEXT_STORAGE_KEY,
  FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY
} from './fhir.service';

const BROADCAST_CHANNEL_NAME = 'channels-tab-session';
const HEARTBEAT_INTERVAL_MS = 1000;
const PING_TIMEOUT_MS = 500;

interface TabSessionMessage
{
  type: 'ping' | 'pong' | 'logout';
  tabId: string;
}

@Injectable({
  providedIn: 'root'
})
export class BrowserTabSessionService
{
  private tabId: string = null;
  private isHandlingRemoteLogout = false;
  private broadcastChannel: BroadcastChannel | null = null;
  private storagePingListener: ((event: StorageEvent) => void) | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> = null;

  constructor(
    private appConfigService: AppConfigService,
    private injector: Injector,
    private router: Router) { }

  /**
   * Runs during APP_INITIALIZER after app config is loaded.
   * Uses AuthenticationService.reset() for full session teardown when a stale tab session is detected.
   */
  public bootstrapSession(): Promise<void>
  {
    return this.runBootstrapSession();
  }

  public destroy(): void
  {
    this.stopHeartbeat();
    this.broadcastChannel?.close();
    this.broadcastChannel = null;

    if (this.storagePingListener)
    {
      window.removeEventListener('storage', this.storagePingListener);
      this.storagePingListener = null;
    }
  }

  /**
   * Notifies other open tabs that this tab logged out so they reset and navigate to login.
   */
  public broadcastLogout(): void
  {
    if (!this.shouldTrackTabs() || !this.tabId)
    {
      return;
    }

    if (typeof BroadcastChannel !== 'undefined' && this.broadcastChannel)
    {
      this.broadcastChannel.postMessage({ type: 'logout', tabId: this.tabId });
      return;
    }

    const logoutKey = `${LS_PREFIX}${TAB_LOGOUT_STORAGE_KEY}`;
    localStorage.setItem(logoutKey, Date.now().toString());
    localStorage.removeItem(logoutKey);
  }

  private async runBootstrapSession(): Promise<void>
  {
    if (!this.shouldTrackTabs())
    {
      return;
    }

    this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try
    {
      this.startTabPresenceListener();

      if (!isNewBrowserTabContext())
      {
        return;
      }

      if (!hasStoredUser())
      {
        return;
      }

      let shouldClearSession = isSessionHeartbeatStale();

      if (!shouldClearSession)
      {
        const otherTabAlive = await this.pingOtherTabs();
        shouldClearSession = !otherTabAlive;
      }

      if (shouldClearSession)
      {
        this.resetAuthenticationSession();
      }
    }
    catch (err)
    {
      console.warn('BrowserTabSessionService: bootstrap failed', err);
    }
    finally
    {
      markTabSessionActive();
      this.startHeartbeat();
    }
  }

  private resetAuthenticationSession(): void
  {
    // Remove persisted user before first AuthenticationService construction to avoid
    // currentUser side effects (permissions load, WebSocket connect) on a stale session.
    clearUserStorage();
    this.injector.get(AuthenticationService).reset(false, false);
  }

  private shouldTrackTabs(): boolean
  {
    const config = this.appConfigService.getConfig();
    if (!config?.cacheTokenToLocalStorage)
    {
      return false;
    }

    const patientContext = this.readSessionValue(FHIR_PATIENT_CONTEXT_STORAGE_KEY);
    const patientIdContext = this.readSessionValue(FHIR_PATIENT_ID_CONTEXT_STORAGE_KEY);
    const fullSite = this.readSessionValue(FHIR_FULL_SITE_STORAGE_KEY);
    const embedded = patientContext !== null && patientIdContext === null;
    return !embedded || !!fullSite;
  }

  private readSessionValue(key: string): unknown
  {
    const raw = sessionStorage.getItem(`${LS_PREFIX}${key}`);
    if (raw === null || raw === '' || raw === 'null')
    {
      return null;
    }

    try
    {
      return JSON.parse(raw);
    }
    catch
    {
      return raw;
    }
  }

  private startTabPresenceListener(): void
  {
    if (typeof BroadcastChannel !== 'undefined')
    {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (event: MessageEvent<TabSessionMessage>) =>
      {
        this.handleTabSessionMessage(event.data);
      };
      return;
    }

    this.storagePingListener = (event: StorageEvent): void =>
    {
      if (event.key === `${LS_PREFIX}${TAB_PING_STORAGE_KEY}` && event.newValue)
      {
        localStorage.setItem(`${LS_PREFIX}${TAB_PONG_STORAGE_KEY}`, this.tabId);
      }
      else if (event.key === `${LS_PREFIX}${TAB_LOGOUT_STORAGE_KEY}` && event.newValue)
      {
        this.handleRemoteLogout();
      }
    };
    window.addEventListener('storage', this.storagePingListener);
  }

  private handleTabSessionMessage(message: TabSessionMessage): void
  {
    if (!message?.tabId || message.tabId === this.tabId)
    {
      return;
    }

    if (message.type === 'ping')
    {
      this.broadcastChannel.postMessage({ type: 'pong', tabId: this.tabId });
    }
    else if (message.type === 'logout')
    {
      this.handleRemoteLogout();
    }
  }

  private handleRemoteLogout(): void
  {
    if (this.isHandlingRemoteLogout)
    {
      return;
    }

    const authService = this.injector.get(AuthenticationService);
    if (authService.isCompletingLogin)
    {
      return;
    }

    this.isHandlingRemoteLogout = true;
    try
    {
      // Always reset local session state. Storage may already be cleared by the tab that broadcast logout.
      authService.reset(false, false, true);

      if (!this.router.url.includes('/login'))
      {
        this.router.navigate(['login']);
      }
    }
    finally
    {
      this.isHandlingRemoteLogout = false;
    }
  }

  private pingOtherTabs(): Promise<boolean>
  {
    if (typeof BroadcastChannel !== 'undefined')
    {
      return this.pingOtherTabsViaBroadcastChannel();
    }
    return this.pingOtherTabsViaStorage();
  }

  private pingOtherTabsViaBroadcastChannel(): Promise<boolean>
  {
    return new Promise(resolve =>
    {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      let otherTabAlive = false;

      channel.onmessage = (event: MessageEvent<TabSessionMessage>) =>
      {
        if (event.data?.type === 'pong' && event.data.tabId !== this.tabId)
        {
          otherTabAlive = true;
        }
      };

      setTimeout(() =>
      {
        channel.postMessage({ type: 'ping', tabId: this.tabId });
      }, 50);

      setTimeout(() =>
      {
        channel.close();
        resolve(otherTabAlive);
      }, PING_TIMEOUT_MS);
    });
  }

  private pingOtherTabsViaStorage(): Promise<boolean>
  {
    return new Promise(resolve =>
    {
      const pongKey = `${LS_PREFIX}${TAB_PONG_STORAGE_KEY}`;
      const pingKey = `${LS_PREFIX}${TAB_PING_STORAGE_KEY}`;
      let otherTabAlive = false;

      localStorage.removeItem(pongKey);

      const onStorage = (event: StorageEvent): void =>
      {
        if (event.key === pongKey && event.newValue && event.newValue !== this.tabId)
        {
          otherTabAlive = true;
        }
      };

      window.addEventListener('storage', onStorage);

      setTimeout(() =>
      {
        localStorage.setItem(pingKey, Date.now().toString());
      }, 50);

      setTimeout(() =>
      {
        window.removeEventListener('storage', onStorage);
        localStorage.removeItem(pingKey);
        localStorage.removeItem(pongKey);
        resolve(otherTabAlive);
      }, PING_TIMEOUT_MS);
    });
  }

  private startHeartbeat(): void
  {
    this.stopHeartbeat();
    updateHeartbeat();
    this.heartbeatTimer = setInterval(() =>
    {
      updateHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void
  {
    if (this.heartbeatTimer !== null)
    {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
